import React, { useState } from "react";
import {
  Select,
  Input,
  Button,
  message,
  Row,
  Col,
  Space,
  Typography,
  Form,
  Table,
} from "antd";
import {
  CheckCircleOutlined,
  CheckOutlined,
  ClearOutlined,
  CopyOutlined,
  ExportOutlined,
  MinusCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

type JsonValue = string | number | boolean | null | JsonObject | JsonValue[];
interface JsonObject {
  [key: string]: JsonValue;
}

const defaultConfigs: Record<string, JsonObject> = {
  Text: { required: "true", min: 3, max: 200 },
  Number: { type: "number", required: "true", min: 3, max: 200 },
  Date: {
    type: "date",
    required: "true",
    minDate: "[Today]",
    maxDate: "[Today+1]",
  },
  Radio: { type: "array", required: "true" },
  SelectSingle: { required: "true" },
  SelectMulti: { type: "array", required: "true", min: 2, max: 4 },
  File: { required: "true" },
  Checkbox: { required: "true" },
  CheckboxMulti: { required: "true" },
  TextArea: { required: "true" },
};

const setValueByPath = (obj: JsonObject, path: string, value: any): void => {
  const keys = path.split(".");
  let current = obj;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      current[key] = value;
    } else {
      current[key] = current[key] ?? {};
      current = current[key] as JsonObject;
    }
  });
};

const FieldValidationBuilder: React.FC = () => {
  const [fieldType, setFieldType] = useState<string>("");
  const [jsonData, setJsonData] = useState<JsonObject>({});
  const [jsonText, setJsonText] = useState<string>("");
  const [form] = Form.useForm();

  const handleLoadJson = () => {
    if (fieldType && defaultConfigs[fieldType]) {
      const json = defaultConfigs[fieldType];
      setJsonData(json);
      setJsonText(JSON.stringify(json, null, 2));
    } else {
      message.error("No default config found for selected field type.");
    }
  };

  const handleSaveJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setJsonData(parsed);
      defaultConfigs[fieldType] = parsed;
      message.success("Saved");
    } catch {
      message.error("Invalid JSON");
    }
  };

  const handleClear = () => {
    setJsonData({});
    setJsonText("");
    form.resetFields();
  };

  const handleBeautify = () => {
    try {
      const cleanedData = cleanEmptyValues(JSON.parse(jsonText));
      const beautified = JSON.stringify(cleanedData, null, 2);
      setJsonText(beautified);
    } catch {
      message.error("Invalid JSON format");
    }
  };

  const handleMinify = () => {
    try {
      const cleanedData = cleanEmptyValues(JSON.parse(jsonText));
      const minified = JSON.stringify(cleanedData);
      setJsonText(minified);
    } catch {
      message.error("Invalid JSON format");
    }
  };

  const handleCopy = () => {
    try {
      const cleanedData = cleanEmptyValues(JSON.parse(jsonText));
      const cleanedJson = JSON.stringify(cleanedData);
      navigator.clipboard
        .writeText(cleanedJson)
        .then(() => message.success("Copied to clipboard!"))
        .catch(() => message.error("Failed to copy JSON"));
    } catch {
      message.error("Invalid JSON format");
    }
  };

  // validate JSON
  const validateJson = () => {
    try {
      const cleanedData = cleanEmptyValues(JSON.parse(jsonText));
      message.success("JSON is in correct format!");
      return JSON.stringify(cleanedData, null, 2);
    } catch {
      message.error("Invalid JSON format");
      return jsonText;
    }
  };

  // Update Query JSON
  const updateQueryJsonInput = () => {
    try {
      const cleanedData = cleanEmptyValues(JSON.parse(jsonText));
      const stringified = JSON.stringify(cleanedData);
      const query = `Update Field SET ValidationRules = '${stringified}' Where Id = ??`;
      navigator.clipboard
        .writeText(query)
        .then(() => {
          message.success("JSON copied to clipboard!");
        })
        .catch(() => {
          message.error("Failed to copy text!");
        });
    } catch {
      message.error("Invalid JSON format");
      return jsonText; // Return original if parsing fails
    }
  };

  // Remove json parameter if value empty
  const cleanEmptyValues = (obj: JsonObject): JsonObject => {
    return Object.fromEntries(
      Object.entries(obj).filter(([key, value]) => {
        // Remove properties where value is empty or null
        if (
          value === "" ||
          value === null ||
          (Array.isArray(value) && value.length === 0)
        ) {
          return false;
        }

        // Handle specific fields like `datasource`
        if (key === "datasource") {
          const { execType, execData } = value as JsonObject;
          if (!execType && !execData) {
            return false; // Remove 'datasource' if all relevant fields are empty
          }
        }
        return true; // Keep the property
      })
    );
  };

  // Placeholder field wise
  const getPlaceholderByFieldType = (
    fieldType: string,
    key: string
  ): string => {
    // Common placeholders used in many types
    const commonPlaceholders: Record<string, string> = {
      required: "Enter true / false",
      min: "Minimum characters allowed",
      max: "Maximum characters allowed",
      Type: "Enter Type",
      placeholder: "Enter placeholder",
    };

    // Specific placeholders per field type
    const placeholdersByType: Record<string, Record<string, string>> = {
      Text: {
        ...commonPlaceholders,
      },
      Number: {
        ...commonPlaceholders,
        min: "Minimum number allowed",
        max: "Maximum number allowed",
      },
      Date: {
        ...commonPlaceholders,
        minDate: 'Minimum date e.g. "[Today]"/"[FieldName]"/"2025-03-31"',
        maxDate: 'Maximum date e.g. "[Today]"/"[FieldName]"/"2025-03-31"',
      },
      Radio: {
        ...commonPlaceholders,
      },
      SelectSingle: {
        required: "Enter true / false",
      },
      SelectMulti: {
        ...commonPlaceholders,
      },
      CheckboxMulti: {
        required: "Enter true / false",
      },
      TextArea: {
        required: "Enter true / false",
      },
      File: {
        required: "Enter true / false",
      },
      Checkbox: {
        required: "Enter true / false",
      },
    };

    // Get the matching placeholder from lookup
    const placeholder =
      placeholdersByType[fieldType]?.[key] || commonPlaceholders[key];

    // Default fallback
    return placeholder || `Enter ${key}`;
  };

  // Note field wise
  const getNoteByFieldType = (
    fieldType: string,
    key: string
  ): React.ReactNode => {
    const boldNote = (text: string) => (
      <span>
        <strong>Note:</strong> {text}
      </span>
    );

    if (fieldType === "Date") {
      if (key === "minDate") {
        return boldNote(
          'Minimum date eg: "[Today]"/"[FieldName]"/"2025-03-31"'
        );
      }

      if (key === "maxDate") {
        return boldNote(
          'Maximum date eg: "[Today]"/"[FieldName]"/"2025-03-31"'
        );
      }
    }

    return "";
  };

  const handleJsonFieldChange = (path: string, value: string | any[]) => {
    const updated = JSON.parse(JSON.stringify(jsonData));
    setValueByPath(updated, path, value);
    setJsonData(updated);
    setJsonText(JSON.stringify(updated, null, 2));
  };

  // Parse multiline input from TextArea
  const parseMultilineInput = (text: string): string[] =>
    text
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

  // Render Table columns for primitive values
  const getColumns = (fieldType: string) => [
    {
      title: "Field",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      render: (text: string, record: { key: string; label: string }) => (
        <>
          <Input
            value={text}
            onChange={(e) => handleJsonFieldChange(record.key, e.target.value)}
            placeholder={getPlaceholderByFieldType(fieldType, record.label)}
            readOnly={record.label === "type"}
          />
          <span>{getNoteByFieldType(fieldType, record.label)}</span>
        </>
      ),
    },
  ];

  const renderEditor = (obj: JsonObject, parentPath = ""): React.ReactNode => {
    const rows: { key: string; label: string; value: string }[] = [];
    const nestedEditors: React.ReactNode[] = [];

    Object.entries(obj).forEach(([key, value]) => {
      const fullPath = parentPath ? `${parentPath}.${key}` : key;

      if (Array.isArray(value)) {
        nestedEditors.push(
          <Col span={24} key={fullPath}>
            <Title level={5}>{key} (Array)</Title>
            <TextArea
              rows={3}
              value={value.map(String).join("\n")} // no redundant Array.isArray check
              onChange={(e) => {
                const items = parseMultilineInput(e.target.value);
                handleJsonFieldChange(fullPath, items);
              }}
            />
          </Col>
        );
      } else if (typeof value === "object" && value !== null) {
        nestedEditors.push(
          <Col span={24} key={fullPath}>
            {renderEditor(value, fullPath)}{" "}
            {/* removed unnecessary type assertion */}
          </Col>
        );
      } else {
        rows.push({
          key: fullPath,
          label: key,
          value: String(value),
        });
      }
    });

    return (
      <Row gutter={16}>
        {rows.length > 0 && (
          <Col span={24}>
            <Table
              scroll={{ x: "max-content" }}
              dataSource={rows}
              pagination={false}
              bordered
              rowKey="key"
              columns={getColumns(fieldType)}
            />
          </Col>
        )}
        {nestedEditors}
      </Row>
    );
  };

  return (
    <div>
      <h4>Field validation JSON builder</h4>
      <Row gutter={16}>
        <Col lg={6} md={24} sm={24} xs={24}>
          <label htmlFor="fieldType">Field Type</label>
          <Select
            id="fieldType"
            value={fieldType}
            onChange={setFieldType}
            showSearch
            style={{ width: "100%" }}
          >
            <Select.Option value="">Select field Type</Select.Option>
            {Object.keys(defaultConfigs).map((type) => (
              <Select.Option key={type} value={type}>
                {type}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Button
          onClick={handleLoadJson}
          color="cyan"
          variant="solid"
          icon={<CheckOutlined />}
          style={{ marginTop: 20 }}
        >
          Get JSON
        </Button>
      </Row>

      <Row style={{ marginTop: 16 }}>
        <Col lg={24} md={24} sm={24} xs={24}>
          <Form.Item>
            <TextArea
              placeholder="Paste JSON here..."
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              rows={10}
            />
            <Col lg={24} md={24} sm={24} xs={24} style={{ marginTop: 10 }}>
              <Space wrap>
                <Button
                  onClick={handleSaveJson}
                  color="primary"
                  variant="solid"
                  icon={<ReloadOutlined />}
                >
                  Load
                </Button>
                <Button
                  danger
                  onClick={handleClear}
                  color="danger"
                  variant="solid"
                  icon={<ClearOutlined />}
                >
                  Clear
                </Button>
                <Button
                  onClick={handleMinify}
                  color="orange"
                  variant="solid"
                  icon={<MinusCircleOutlined />}
                >
                  Minify
                </Button>
                <Button
                  onClick={handleBeautify}
                  color="purple"
                  variant="solid"
                  icon={<ExportOutlined />}
                >
                  Beautify
                </Button>
                <Button
                  onClick={handleCopy}
                  color="pink"
                  variant="solid"
                  icon={<CopyOutlined />}
                >
                  Copy
                </Button>
                <Button
                  onClick={validateJson}
                  color="danger"
                  variant="solid"
                  icon={<CheckCircleOutlined />}
                >
                  Validate JSON
                </Button>
                <Button
                  onClick={updateQueryJsonInput}
                  color="cyan"
                  variant="solid"
                  icon={<CheckOutlined />}
                >
                  Copy JSON Query
                </Button>
              </Space>
            </Col>
          </Form.Item>
        </Col>
      </Row>

      <Row style={{ marginTop: 16 }}>
        <Col
          xs={24} // Extra small (mobile): full width
          sm={24} // Small (tablet): full width
          md={12} // Medium (desktop): half width
          lg={6} // Large (large desktop): one-third width
          xl={6}
        >
          <Form layout="vertical" form={form}>
            <Row gutter={16}>{renderEditor(jsonData)}</Row>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default FieldValidationBuilder;
